import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { LanguageContext } from '../../context/LanguageContext';
import { useLocation } from 'react-router-dom';
import metaData from '../../utils/meta.json';

/**
 * meta.json dosyasını kullanarak SEO meta etiketlerini yöneten gelişmiş Helmet bileşeni
 * Her sayfa için özel meta etiketlerini uygular
 * 
 * @param {string} pageName - Sayfanın meta.json içindeki anahtar adı
 * @param {object} customMeta - Özel meta bilgileri (opsiyonel)
 * @returns {JSX.Element} - Helmet bileşeni
 */
const MetaHelmet = ({ pageName = 'home', customMeta = {} }) => {
  const { currentLang } = useContext(LanguageContext);
  const location = useLocation();
  
  // meta.json'dan sayfa meta verilerini al
  const pageMetaData = metaData[pageName] || metaData.home;
  
  // Özel meta verileri ile birleştir
  const meta = {
    ...pageMetaData,
    ...customMeta
  };
  
  // Canonical URL'yi oluştur
  const baseUrl = 'https://monaklinik.com';
  const currentPath = location.pathname;
  const canonicalUrl = `${baseUrl}${currentPath}`;
  
  // Dil bazlı URL'leri oluştur
  const trUrl = currentLang === 'en' ? canonicalUrl.replace('/en', '') : canonicalUrl;
  const enUrl = currentLang === 'tr' ? `${baseUrl}/en${currentPath}` : canonicalUrl;
  
  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang etiketleri */}
      <link rel="alternate" hreflang="tr" href={trUrl} />
      <link rel="alternate" hreflang="en" href={enUrl} />
      <link rel="alternate" hreflang="x-default" href={trUrl} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={meta.ogTitle || meta.title} />
      <meta property="og:description" content={meta.ogDescription || meta.description} />
      <meta property="og:url" content={meta.ogUrl || canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={currentLang === 'tr' ? 'tr_TR' : 'en_US'} />
      {meta.ogImage && <meta property="og:image" content={`${baseUrl}${meta.ogImage}`} />}
      <meta property="og:site_name" content="Mona Klinik" />
      
      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.twitterTitle || meta.title} />
      <meta name="twitter:description" content={meta.twitterDescription || meta.description} />
      {meta.twitterImage && <meta name="twitter:image" content={`${baseUrl}${meta.twitterImage}`} />}
      
      {/* Ek Meta Etiketler */}
      <meta name="author" content="Mona Klinik" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Mobil Uyumluluk */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta name="theme-color" content="#0e82fd" />
    </Helmet>
  );
};

export default MetaHelmet;